import {useCallback, useSyncExternalStore} from 'react'
import {createEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import {copyClientCommand} from '@github-ui/ahp-session-client/client-interaction-observer'
import {filterContributedInstructions} from '@github-ui/ahp-session-client/contributed-instructions'
import type {
  EstablishedAhpDirectTurnTelemetry,
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  DirectTurnTelemetryContext,
} from '@github-ui/ahp-session-client/contracts'
import type {ManagedEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {
  discardRelayInstructionRulesWhenUnused,
  getRelayEntry,
  reportRelayError,
} from '@github-ui/ahp-relay/relay-registry'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  discardInstructionRuleSession,
  InstructionRuleSessionPublisher,
} from '@github-ui/copilot-chat/utils/instruction-rules-publisher'
import {createCustomAgentPromptPreparer} from '@github-ui/ahp-session-client/custom-agent-selection'
import {ensureSessionMode} from '@github-ui/ahp-session-client/session-mode'
import {
  isSameDispatchedAttempt,
  isSubmissionRecoveryEnabled,
  submissionRecoveryMessage,
} from '@github-ui/ahp-session-client/submission-recovery'

import {createMissionControlEstablishedAhpBootstrap} from '../ahp/established-ahp-bootstrap'
import {TaskLaunchAttempt} from '../ahp/task-launch-attempt'
import {createAhpRelayDiagnostics} from '../ahp/ahp-debug-log'
import {
  ENVIRONMENT_TASK_LAUNCH_ACTIVE_TTL_MS,
  type EnvironmentTaskLaunchRecord,
  type EnvironmentTaskLaunchTask,
  clearEnvironmentTaskLaunch,
  inspectEnvironmentTaskLaunch,
  readEnvironmentTaskLaunch,
  replaceEnvironmentTaskLaunch,
  resolveEnvironmentTaskTelemetryContext,
  restartExpiredEnvironmentTaskLaunch,
} from '../ahp/environment-task-launch-journal'
import {beginTaskStartup, completeTaskStartup, failTaskStartup, getTaskStartup} from '../ahp/task-startup-store'
import {
  applyEnvironmentTaskStartupApprovalMode,
  applyEnvironmentTaskStartupMode,
} from '../ahp/environment-task-startup-configuration'
import {buildAhpRecoveryCreateSession} from '../components/task-view/ahp-session-recovery'
import {bindUserStartedTurn, markUserStartedTurn, unmarkUserStartedTurn} from '../utils/artifacts/user-started-turns'
import {mcFetcher} from '../utils/mc-fetcher'
import {createCopilotChatTurnLatencyTracker} from '../ahp/turn-latency'
import {isEnvironmentStatusEnabled} from './use-environment'
import {buildCreatePullRequestClientTool} from '../ahp/create-pull-request-tool'

const RELEASE_GRACE_MS = 5_000

export type EnvironmentTaskLaunchState =
  | {status: 'unchecked'}
  | {status: 'unowned'}
  | {status: 'pending'; prompt: EnvironmentTaskLaunchRecord['prompt']}
  | {
      status: 'failed'
      prompt: EnvironmentTaskLaunchRecord['prompt']
      error: string
      retry: () => void
      recover?: () => void
      recovering?: boolean
    }
  | {status: 'expired'; prompt: EnvironmentTaskLaunchRecord['prompt']; error: string; restart: () => void}
  | {status: 'accepted'; session: EstablishedAhpSession; bootstrap: EstablishedAhpSessionBootstrap}

interface LaunchEntry {
  taskId: string
  record: EnvironmentTaskLaunchRecord
  persisted: boolean
  authoritativeTask?: EnvironmentTaskLaunchTask
  awaitingAuthoritativeTask: boolean
  snapshot: EnvironmentTaskLaunchState
  listeners: Set<() => void>
  consumers: number
  releaseTimer?: ReturnType<typeof setTimeout>
  expiryTimer?: ReturnType<typeof setTimeout>
  disposed: boolean
  attempt?: TaskLaunchAttempt
  bootstrap?: EstablishedAhpSessionBootstrap
  directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  directTurnTelemetryAttempt?: object
  ownerBoundaryCleanup?: () => void
  firstTurnId?: string
}

const entries = new Map<string, LaunchEntry>()
const checkedTaskIds = new Set<string>()
const pendingInlineOwners = new Map<
  string,
  {record: EnvironmentTaskLaunchRecord; snapshot: EnvironmentTaskLaunchState}
>()
const waitingListeners = new Map<string, Set<() => void>>()

function notify(entry: LaunchEntry): void {
  for (const listener of entry.listeners) listener()
}

function setSnapshot(entry: LaunchEntry, snapshot: EnvironmentTaskLaunchState): void {
  if (entry.disposed) return
  if (entry.expiryTimer) {
    clearTimeout(entry.expiryTimer)
    entry.expiryTimer = undefined
  }
  entry.snapshot = snapshot
  if (snapshot.status === 'failed') scheduleExpiry(entry)
  if (snapshot.status === 'accepted') {
    if (entry.releaseTimer) {
      clearTimeout(entry.releaseTimer)
      entry.releaseTimer = undefined
    }
    observeUnclaimedFirstTurn(entry)
  }
  notify(entry)
}

function failureMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function isCurrentAttempt(entry: LaunchEntry, attempt: TaskLaunchAttempt): boolean {
  return !entry.disposed && !attempt.disposed && entry.attempt === attempt
}

function assertCurrentAttempt(entry: LaunchEntry, attempt: TaskLaunchAttempt): void {
  if (!isCurrentAttempt(entry, attempt)) throw new Error('Environment task startup attempt was superseded.')
}

function sendLaunchEvent(
  entry: LaunchEntry,
  attempt: TaskLaunchAttempt | undefined,
  stage: string,
  additional = {},
): void {
  if (attempt && !isCurrentAttempt(entry, attempt)) return
  sendEvent('agent_sessions.environment_task_launch', {
    stage,
    task_id: entry.taskId,
    request_id: entry.record.requestId,
    journal_age_ms: Date.now() - entry.record.createdAt,
    ...additional,
  })
}

function resolvePendingTelemetry(entry: LaunchEntry, context: DirectTurnTelemetryContext | null): void {
  const telemetry = entry.directTurnTelemetry
  entry.directTurnTelemetry = undefined
  entry.directTurnTelemetryAttempt = undefined
  const seed = entry.record.directTurnTelemetrySeed
  if (seed) telemetry?.submissionContextResolved?.(seed.requestId, context)
}

function cleanupAttempt(entry: LaunchEntry, attempt: TaskLaunchAttempt, {accepted = false} = {}): void {
  if (attempt.disposed) return
  // Retire subscriptions before host cleanup, then release the bootstrap's lease.
  const bootstrap = attempt.takeBootstrap()
  try {
    attempt.dispose()
    if (entry.attempt === attempt) {
      const currentRelayEntry = getRelayEntry(entry.taskId)
      const relayEntry =
        currentRelayEntry?.environmentId === entry.record.identity.environmentId &&
        currentRelayEntry.sessionId === entry.record.identity.sessionId &&
        currentRelayEntry.channel === undefined
          ? currentRelayEntry
          : undefined
      discardRelayInstructionRulesWhenUnused(entry.taskId, relayEntry, () =>
        discardInstructionRuleSession(entry.taskId),
      )
    }
    if (!accepted && entry.attempt === attempt && entry.awaitingAuthoritativeTask) {
      resolvePendingTelemetry(entry, null)
    }
  } finally {
    bootstrap?.dispose()
    if (entry.attempt === attempt) entry.attempt = undefined
  }
}

function invalidateAttempt(entry: LaunchEntry): void {
  if (entry.attempt) cleanupAttempt(entry, entry.attempt)
}

function clearOwnerBoundary(entry: LaunchEntry): void {
  entry.ownerBoundaryCleanup?.()
  entry.ownerBoundaryCleanup = undefined
}

function disposeEntry(entry: LaunchEntry): void {
  if (entry.disposed) return
  entry.disposed = true
  if (entry.releaseTimer) clearTimeout(entry.releaseTimer)
  if (entry.expiryTimer) clearTimeout(entry.expiryTimer)
  clearOwnerBoundary(entry)
  invalidateAttempt(entry)
  if (entry.awaitingAuthoritativeTask && entry.record.directTurnTelemetrySeed) {
    resolvePendingTelemetry(entry, null)
  }
  entry.bootstrap?.dispose()
  entry.bootstrap = undefined
  entry.directTurnTelemetry = undefined
  entry.directTurnTelemetryAttempt = undefined
  entries.delete(entry.taskId)
}

function scheduleRelease(entry: LaunchEntry): void {
  if (entry.disposed || entry.consumers > 0 || entry.releaseTimer) return
  entry.releaseTimer = setTimeout(() => {
    entry.releaseTimer = undefined
    if (entry.consumers > 0 || entry.disposed) return
    if (retainedSubmission(entry)) return
    disposeEntry(entry)
  }, RELEASE_GRACE_MS)
}

function observeUnclaimedFirstTurn(entry: LaunchEntry): void {
  if (entry.disposed || entry.consumers > 0 || entry.snapshot.status !== 'accepted') return
  if (!entry.firstTurnId) {
    scheduleRelease(entry)
    return
  }
  clearOwnerBoundary(entry)
  const {session} = entry.snapshot
  const firstTurnId = entry.firstTurnId
  const releaseAfterTerminal = () => {
    if (entry.disposed || entry.consumers > 0) return
    const state = session.view.getSnapshot().state
    if (state?.activeTurn?.id === firstTurnId) {
      if (entry.releaseTimer) {
        clearTimeout(entry.releaseTimer)
        entry.releaseTimer = undefined
      }
      return
    }
    if (state?.turns.some(turn => turn.id === firstTurnId)) scheduleRelease(entry)
  }
  const releaseAfterAbandonment = () => {
    if (entry.disposed || entry.consumers > 0) return
    const lifecycle = session.lifecycle.getState()
    if (lifecycle === 'failed' || lifecycle === 'disposed') scheduleRelease(entry)
  }
  const unsubscribeView = session.view.subscribe(releaseAfterTerminal)
  const unsubscribeLifecycle = session.lifecycle.subscribe(releaseAfterAbandonment)
  entry.ownerBoundaryCleanup = () => {
    unsubscribeView()
    unsubscribeLifecycle()
  }
  releaseAfterTerminal()
  releaseAfterAbandonment()
}

export function expectInlineEnvironmentTaskLaunchOwner(record: EnvironmentTaskLaunchRecord): void {
  pendingInlineOwners.set(record.taskId, {
    record,
    snapshot: {status: 'pending', prompt: record.prompt},
  })
  checkedTaskIds.delete(record.taskId)
  for (const listener of waitingListeners.get(record.taskId) ?? []) listener()
}

export function registerInlineEnvironmentTaskLaunchOwner(
  record: EnvironmentTaskLaunchRecord,
  session: EstablishedAhpSession,
  bootstrap: EstablishedAhpSessionBootstrap,
  firstTurnId: string | undefined,
  directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry,
  submission?: ManagedEstablishedAhpInitialSubmissionCoordinator,
): void {
  pendingInlineOwners.delete(record.taskId)
  const hasUnresolvedTelemetrySeed = !!record.directTurnTelemetrySeed && !record.directTurnTelemetrySeed.context
  const entry: LaunchEntry = {
    taskId: record.taskId,
    record,
    persisted: false,
    awaitingAuthoritativeTask: !!directTurnTelemetry && hasUnresolvedTelemetrySeed,
    snapshot: {status: 'accepted', session, bootstrap},
    listeners: new Set(waitingListeners.get(record.taskId) ?? []),
    consumers: waitingListeners.get(record.taskId)?.size ?? 0,
    disposed: false,
    bootstrap,
    directTurnTelemetry,
    directTurnTelemetryAttempt: directTurnTelemetry ? {} : undefined,
    firstTurnId,
    ...(submission ? {attempt: new TaskLaunchAttempt(record.taskId, bootstrap, submission), bootstrap: undefined} : {}),
  }
  if (submission) entry.snapshot = failureSnapshot(entry, submissionRecoveryMessage('unknown'))
  waitingListeners.delete(record.taskId)
  entries.set(record.taskId, entry)
  if (entry.attempt) observeRetainedSubmission(entry, entry.attempt, session)
  if (entry.consumers === 0) {
    if (submission) scheduleRelease(entry)
    else observeUnclaimedFirstTurn(entry)
  }
  notify(entry)
}

export function abandonInlineEnvironmentTaskLaunchOwner(taskId: string): void {
  if (!pendingInlineOwners.delete(taskId)) return
  checkedTaskIds.add(taskId)
  for (const listener of waitingListeners.get(taskId) ?? []) listener()
  waitingListeners.delete(taskId)
}

function updateRecord(
  entry: LaunchEntry,
  update: (record: EnvironmentTaskLaunchRecord) => EnvironmentTaskLaunchRecord,
): EnvironmentTaskLaunchRecord | null {
  const expected = entry.record
  if (!entry.persisted) {
    if (inspectEnvironmentTaskLaunch(entry.taskId).status !== 'missing') return null
    entry.record = {...update(expected), revision: expected.revision + 1}
    return entry.record
  }
  if (replaceEnvironmentTaskLaunch(entry.taskId, update, expected) !== null) return null
  const record = readEnvironmentTaskLaunch(entry.taskId)
  if (!record || record.launchId !== entry.record.launchId) return null
  entry.record = record
  return record
}

function retainedSubmission(entry: LaunchEntry) {
  const submission = entry.attempt?.submission
  return isSubmissionRecoveryEnabled() && submission?.recovery?.getSnapshot()?.dispatch ? submission : undefined
}

function observeRetainedSubmission(
  entry: LaunchEntry,
  attempt: TaskLaunchAttempt,
  session = attempt.bootstrap?.session,
): void {
  const recovery = retainedSubmission(entry)?.recovery
  const before = recovery?.getSnapshot()
  if (!recovery || !before?.dispatch || !session) return
  const settleSubmission = () => {
    if (!isCurrentAttempt(entry, attempt) || entries.get(entry.taskId) !== entry) return
    const settled = recovery.getSnapshot()
    if (
      !isSubmissionRecoveryEnabled() ||
      (settled?.phase !== 'accepted' && settled?.phase !== 'rejected') ||
      !isSameDispatchedAttempt(before, settled) ||
      settled.requestId !== entry.record.requestId
    ) {
      return
    }
    const record = inspectEnvironmentTaskLaunch(entry.taskId)
    if (
      record.status !== 'missing' &&
      (record.record.launchId !== entry.record.launchId ||
        record.record.requestId !== entry.record.requestId ||
        record.record.revision !== entry.record.revision)
    ) {
      return
    }
    if (settled.phase === 'accepted') completeLaunch(entry, attempt, session)
    else rejectLaunch(entry, attempt)
  }
  attempt.observeRecovery(recovery, settleSubmission)
}

function rejectLaunch(entry: LaunchEntry, attempt: TaskLaunchAttempt): void {
  cleanupAttempt(entry, attempt)
  markFailed(
    entry,
    undefined,
    new Error('The host rejected the first message. Your draft is still available.'),
    false,
    false,
  )
  scheduleRelease(entry)
}

function failureSnapshot(entry: LaunchEntry, message: string, recovering = false): EnvironmentTaskLaunchState {
  return {
    status: 'failed',
    prompt: entry.record.prompt,
    error: message,
    retry: () => retry(entry),
    ...(retainedSubmission(entry) ? {recover: () => void recover(entry), recovering} : {}),
  }
}

function expiredMessage(record: EnvironmentTaskLaunchRecord): string {
  return record.phase === 'dispatching'
    ? 'Startup may already have reached the environment. Restarting may send the first message again.'
    : 'Startup recovery expired. Restart startup to try again.'
}

function expiredSnapshot(entry: LaunchEntry): EnvironmentTaskLaunchState {
  return {
    status: 'expired',
    prompt: entry.record.prompt,
    error: expiredMessage(entry.record),
    restart: () => restart(entry),
  }
}

function transitionToExpired(entry: LaunchEntry): boolean {
  const result = inspectEnvironmentTaskLaunch(entry.taskId)
  if (
    result.status !== 'expired' ||
    result.record.launchId !== entry.record.launchId ||
    result.record.revision !== entry.record.revision
  ) {
    return false
  }
  entry.record = result.record
  setSnapshot(entry, expiredSnapshot(entry))
  return true
}

function scheduleExpiry(entry: LaunchEntry): void {
  if (!entry.persisted || retainedSubmission(entry)) return
  const delay = Math.max(0, entry.record.expiresAt - Date.now())
  entry.expiryTimer = setTimeout(() => {
    entry.expiryTimer = undefined
    if (entry.disposed || entry.snapshot.status !== 'failed') return
    if (transitionToExpired(entry)) return
    const result = inspectEnvironmentTaskLaunch(entry.taskId)
    if (result.status === 'expired' && result.record.launchId === entry.record.launchId) {
      entry.record = result.record
      setSnapshot(entry, expiredSnapshot(entry))
      return
    }
    if (result.status !== 'available' || result.record.launchId !== entry.record.launchId) return
    entry.record = result.record
    scheduleExpiry(entry)
  }, delay)
}

function markFailed(
  entry: LaunchEntry,
  attempt: TaskLaunchAttempt | undefined,
  error: unknown,
  preserveDispatching = false,
  reportRelayFailure = true,
): void {
  if (attempt && !isCurrentAttempt(entry, attempt)) return
  const failure = failureMessage(error)
  const phase = retainedSubmission(entry)?.recovery?.getSnapshot()?.phase
  const message = phase
    ? submissionRecoveryMessage(phase === 'accepted' || phase === 'rejected' ? phase : 'unknown')
    : preserveDispatching
      ? 'Startup may have reached the environment. Retry explicitly before sending the first message again.'
      : failure
  sendLaunchEvent(entry, attempt, 'failed', {
    phase: entry.record.phase,
    ambiguous_dispatch: preserveDispatching,
    message: failure,
  })
  failTaskStartup(entry.taskId, error)
  let expired = false
  if (!preserveDispatching) {
    const record = updateRecord(entry, current => ({
      ...current,
      phase: 'failed',
      failure: {message, occurredAt: Date.now()},
    }))
    expired = !record && transitionToExpired(entry)
  }
  // Submission recovery failures belong to the recovery notice, not persistent task errors.
  if (!phase && reportRelayFailure) reportRelayError(entry.taskId, error)
  if (!expired) setSnapshot(entry, failureSnapshot(entry, message))
}

function retry(entry: LaunchEntry): void {
  if (entry.disposed || entry.snapshot.status === 'pending') return
  if (retainedSubmission(entry)) {
    void recover(entry)
    return
  }
  if (entry.persisted && entry.record.expiresAt <= Date.now()) {
    restart(entry)
    return
  }
  invalidateAttempt(entry)
  entry.bootstrap?.dispose()
  entry.bootstrap = undefined
  const now = Date.now()
  const record = updateRecord(entry, current => ({
    ...current,
    phase: 'task-created',
    expiresAt: now + ENVIRONMENT_TASK_LAUNCH_ACTIVE_TTL_MS,
    failure: undefined,
  }))
  if (!record) {
    markFailed(entry, undefined, new Error('The environment task startup record changed before retry.'))
    return
  }
  setSnapshot(entry, {
    status: 'pending',
    prompt: record.prompt,
  })
  void start(entry)
}

async function recover(entry: LaunchEntry): Promise<void> {
  if (entry.disposed || entry.snapshot.status !== 'failed' || entry.snapshot.recovering) return
  const attempt = entry.attempt
  const submission = retainedSubmission(entry)
  const before = submission?.recovery?.getSnapshot()
  if (!attempt || !submission?.recover || !before?.dispatch) {
    setSnapshot(
      entry,
      failureSnapshot(entry, 'Submission status checking is unavailable. Your draft is still available.'),
    )
    return
  }
  setSnapshot(entry, failureSnapshot(entry, entry.snapshot.error, true))
  try {
    sendEvent('copilot_composer.submission_recovery', {action: 'reconnect'})
    let session: EstablishedAhpSession | undefined
    try {
      session = await submission.recover()
    } catch (error) {
      // A rejection can arrive while recovery waits for acceptance.
      if (submission.recovery?.getSnapshot()?.phase !== 'rejected') throw error
    }
    if (!isCurrentAttempt(entry, attempt) || entries.get(entry.taskId) !== entry) return
    const record = inspectEnvironmentTaskLaunch(entry.taskId)
    if (
      record.status !== 'missing' &&
      (record.record.launchId !== entry.record.launchId ||
        record.record.requestId !== entry.record.requestId ||
        record.record.revision !== entry.record.revision)
    ) {
      throw new Error('The startup record changed while checking the submission.')
    }
    const settled = submission.recovery?.getSnapshot()
    if (!isSameDispatchedAttempt(before, settled)) {
      throw new Error('The submission attempt changed while checking its status.')
    }
    sendEvent('copilot_composer.submission_recovery', {action: 'reconciled', outcome: settled.phase})
    if (settled.phase === 'accepted') {
      if (!session) throw new Error('The startup session is unavailable.')
      completeLaunch(entry, attempt, session)
    } else if (settled.phase === 'rejected') {
      rejectLaunch(entry, attempt)
    } else {
      throw new Error('The host has not confirmed this submission.')
    }
  } catch (error) {
    if (!isCurrentAttempt(entry, attempt) || entries.get(entry.taskId) !== entry) return
    markFailed(entry, attempt, error, true)
    sendEvent('copilot_composer.submission_recovery', {action: 'recovery_failed'})
  }
}

function completeLaunch(entry: LaunchEntry, attempt: TaskLaunchAttempt, session: EstablishedAhpSession): void {
  const bootstrap = attempt.bootstrap
  if (!bootstrap) throw new Error('The startup session is unavailable.')
  const accepted = session.input.recovery?.getSnapshot()
  if (accepted?.requestId === entry.record.requestId && accepted.phase === 'accepted' && accepted.dispatch) {
    entry.firstTurnId = accepted.dispatch.turnId
  }
  completeTaskStartup(entry.taskId)
  clearEnvironmentTaskLaunch(entry.taskId, entry.record)
  entry.bootstrap?.dispose()
  entry.bootstrap = attempt.takeBootstrap()
  sendLaunchEvent(entry, attempt, 'first_turn_accepted')
  setSnapshot(entry, {status: 'accepted', session, bootstrap})
  cleanupAttempt(entry, attempt, {accepted: true})
}

function restart(entry: LaunchEntry): void {
  if (entry.disposed || entry.snapshot.status === 'pending') return
  invalidateAttempt(entry)
  entry.bootstrap?.dispose()
  entry.bootstrap = undefined
  const restarted = restartExpiredEnvironmentTaskLaunch(entry.taskId, entry.record)
  if (!restarted) {
    const result = inspectEnvironmentTaskLaunch(entry.taskId)
    if (result.status === 'expired' && result.record.launchId === entry.record.launchId) {
      entry.record = result.record
      setSnapshot(entry, expiredSnapshot(entry))
    } else {
      setSnapshot(entry, failureSnapshot(entry, 'The environment task startup record changed before restart.'))
    }
    return
  }
  const result = inspectEnvironmentTaskLaunch(entry.taskId)
  const enriched =
    result.status === 'available' ? enrichDirectTurnTelemetryContext(result, entry.authoritativeTask) : undefined
  if (!enriched) {
    setSnapshot(entry, failureSnapshot(entry, 'The environment task startup record changed before restart.'))
    return
  }
  entry.record = enriched.record
  entry.awaitingAuthoritativeTask =
    entry.awaitingAuthoritativeTask &&
    !!entry.record.directTurnTelemetrySeed &&
    !entry.record.directTurnTelemetrySeed.context
  setSnapshot(entry, {
    status: 'pending',
    prompt: entry.record.prompt,
  })
  void start(entry)
}

async function start(entry: LaunchEntry): Promise<void> {
  if (entry.disposed || entry.attempt) return
  const attempt = new TaskLaunchAttempt(entry.taskId)
  entry.attempt = attempt
  const initialRecord = entry.record
  sendLaunchEvent(entry, attempt, 'recovery_started')

  const previousLaunch = getTaskStartup(entry.taskId)?.launch
  beginTaskStartup(
    entry.taskId,
    initialRecord.prompt,
    initialRecord.recovery.managedSandbox
      ? {
          kind: 'managed-sandbox',
          environmentId: initialRecord.identity.environmentId,
          ownerLogin:
            initialRecord.recovery.repositoryNwo?.split('/')[0] ||
            (previousLaunch?.environmentId === initialRecord.identity.environmentId
              ? previousLaunch.ownerLogin
              : undefined),
        }
      : undefined,
  )
  if (initialRecord.phase === 'dispatching') {
    markFailed(
      entry,
      attempt,
      new Error('Startup may have reached the environment. Retry explicitly before sending the first message again.'),
      true,
    )
    cleanupAttempt(entry, attempt)
    return
  }
  if (initialRecord.phase === 'failed') {
    markFailed(
      entry,
      attempt,
      new Error(initialRecord.failure?.message ?? 'The environment task could not be started.'),
    )
    cleanupAttempt(entry, attempt)
    return
  }

  const createSession = buildAhpRecoveryCreateSession({
    environmentKind: initialRecord.recovery.managedSandbox ? 'managed-sandbox' : 'user-local',
    nwo: initialRecord.recovery.repositoryNwo,
    cloneUrl: initialRecord.recovery.repositoryCloneUrl,
    baseBranch: initialRecord.recovery.baseRef,
    scopeLogin: initialRecord.recovery.scopeLogin,
    mode: initialRecord.startupMode,
    approvalMode: initialRecord.approvalMode,
  })
  const preparingRecord = updateRecord(entry, current => ({
    ...current,
    phase: 'preparing',
    expiresAt: Date.now() + ENVIRONMENT_TASK_LAUNCH_ACTIVE_TTL_MS,
    failure: undefined,
  }))
  if (!preparingRecord) {
    markFailed(entry, attempt, new Error('The environment task startup record changed before recovery.'))
    cleanupAttempt(entry, attempt)
    return
  }

  const instructionScope = initialRecord.recovery.repositoryNwo ? 'repository' : 'chat'
  let dispatching = false
  let launchSubmissionId: number | undefined
  try {
    assertCurrentAttempt(entry, attempt)
    const instructionRulePublisher = new InstructionRuleSessionPublisher(instructionScope)
    instructionRulePublisher.activate(entry.taskId)
    attempt.attachInstructionRulePublisher(instructionRulePublisher)
    const directTurnTelemetry = initialRecord.recovery.managedSandbox
      ? createCopilotChatTurnLatencyTracker()
      : undefined
    entry.directTurnTelemetry = directTurnTelemetry
    entry.directTurnTelemetryAttempt = directTurnTelemetry ? {} : undefined
    const bootstrap = createMissionControlEstablishedAhpBootstrap({
      provisionSession: async () => {
        throw new Error('Environment task recovery cannot provision a second task.')
      },
      createRelayOptions: (identity, request) => ({
        fetcher: mcFetcher,
        createSink: () => new AhpChatStore(),
        acceptInstructionRules: true,
        hostTools:
          initialRecord.recovery.baselineHostTools || initialRecord.recovery.managedSandbox
            ? initialRecord.recovery.repositoryNwo &&
              isFeatureEnabled('copilot_mission_control_create_pull_request_client_tool')
              ? {
                  kind: 'host-bound',
                  tools: [
                    buildCreatePullRequestClientTool({
                      context: {
                        repoNwo: initialRecord.recovery.repositoryNwo,
                        taskId: entry.taskId,
                        sessionId: identity.sessionId,
                        environmentId: identity.environmentId,
                      },
                    }),
                  ],
                }
              : {kind: 'baseline-at-create'}
            : {kind: 'none'},
        ...createAhpRelayDiagnostics(identity.taskId ?? identity.sessionId),
        createSession: request?.mode === 'recover' ? createSession : undefined,
      }),
      directTurnTelemetry,
    })
    attempt.attachBootstrap(bootstrap)
    const session = await bootstrap.establish({
      mode: 'recover',
      identity: preparingRecord.identity,
      awaitIntentAfterLive: initialRecord.recovery.managedSandbox && isEnvironmentStatusEnabled(),
    })
    assertCurrentAttempt(entry, attempt)
    sendLaunchEvent(entry, attempt, 'relay_connected')
    if (session.view.getSnapshot().passive) throw new Error('This environment is read-only.')

    // An explicit approval mode is policy-sensitive: the coupled `setApproveAll` write in
    // `applyEnvironmentTaskStartupMode` can reject on a host whose approval mode is locked,
    // which would stop recovery before the host-aware approval helper ever runs. Mirror the
    // inline create-path branch (see `use-create-environment-task.ts`) and keep the mode and
    // approval-mode writes independent whenever an approval mode was explicitly selected.
    if (preparingRecord.approvalMode) {
      await Promise.all([
        ensureSessionMode(session, preparingRecord.startupMode),
        applyEnvironmentTaskStartupApprovalMode(session, preparingRecord.approvalMode),
      ])
    } else {
      await applyEnvironmentTaskStartupMode(session, preparingRecord.startupMode)
    }
    assertCurrentAttempt(entry, attempt)

    const submission = createEstablishedAhpInitialSubmissionCoordinator({
      bootstrap,
      identity: preparingRecord.identity,
    })
    attempt.attachSubmission(submission)
    const customAgent = preparingRecord.customAgent
    const contributedInstructions = filterContributedInstructions(
      preparingRecord.prompt.contributedInstructions ?? [],
      instructionScope,
    )
    const prompt =
      contributedInstructions.length < (preparingRecord.prompt.contributedInstructions?.length ?? 0)
        ? copyClientCommand(
            preparingRecord.prompt,
            {
              ...preparingRecord.prompt,
              contributedInstructions: contributedInstructions.length > 0 ? contributedInstructions : undefined,
            },
            'scope_contributed_instructions',
          )
        : preparingRecord.prompt
    const acceptedSession = await submission.submit({
      prompt,
      origin: 'recovery',
      requestId: preparingRecord.requestId,
      ...(directTurnTelemetry
        ? {
            ...(preparingRecord.directTurnTelemetrySeed
              ? {directTurnTelemetrySurface: preparingRecord.directTurnTelemetrySeed.surface}
              : {}),
            directTurnTelemetryContext: preparingRecord.directTurnTelemetrySeed
              ? preparingRecord.directTurnTelemetrySeed.context
              : null,
            ...(preparingRecord.directTurnTelemetrySeed &&
            !preparingRecord.directTurnTelemetrySeed.context &&
            entry.awaitingAuthoritativeTask
              ? {directTurnTelemetryContextPending: true}
              : {}),
            ...(preparingRecord.directTurnTelemetrySeed
              ? {directTurnTelemetryTiming: preparingRecord.directTurnTelemetrySeed.timing}
              : {}),
          }
        : {}),
      ...(customAgent
        ? {
            preparePrompt: createCustomAgentPromptPreparer(customAgent),
          }
        : {}),
      beforeDispatch: async () => {
        assertCurrentAttempt(entry, attempt)
        const record = updateRecord(entry, current => ({
          ...current,
          phase: 'dispatching',
          failure: undefined,
        }))
        if (!record) throw new Error('The environment task startup record changed before dispatch.')
        dispatching = true
        launchSubmissionId = markUserStartedTurn(entry.taskId)
        sendLaunchEvent(entry, attempt, 'first_turn_dispatching')
      },
      afterDispatch: receipt => {
        if (launchSubmissionId !== undefined) bindUserStartedTurn(entry.taskId, launchSubmissionId, receipt)
      },
      onTurnDispatched: turnId => {
        if (isCurrentAttempt(entry, attempt)) entry.firstTurnId = turnId
      },
    })
    assertCurrentAttempt(entry, attempt)
    if (acceptedSession.view.getSnapshot().passive) throw new Error('This environment is read-only.')

    completeLaunch(entry, attempt, acceptedSession)
  } catch (error) {
    // The submission is armed in `beforeDispatch`, so a startup that fails after it
    // has to retire it. The user is shown the failure either way, and opening an
    // artifact behind that message would contradict it.
    if (launchSubmissionId !== undefined) unmarkUserStartedTurn(entry.taskId, launchSubmissionId)
    if (isCurrentAttempt(entry, attempt)) markFailed(entry, attempt, error, dispatching)
    if (retainedSubmission(entry)) observeRetainedSubmission(entry, attempt)
    else cleanupAttempt(entry, attempt)
  }
}

function createEntry(
  record: EnvironmentTaskLaunchRecord,
  expired = false,
  awaitingAuthoritativeTask = false,
  authoritativeTask?: EnvironmentTaskLaunchTask,
): LaunchEntry {
  const entry: LaunchEntry = {
    taskId: record.taskId,
    record,
    persisted: true,
    authoritativeTask,
    awaitingAuthoritativeTask:
      awaitingAuthoritativeTask && !!record.directTurnTelemetrySeed && !record.directTurnTelemetrySeed.context,
    snapshot: expired ? uncheckedState : {status: 'pending', prompt: record.prompt},
    listeners: new Set(),
    consumers: 0,
    disposed: false,
  }
  checkedTaskIds.delete(record.taskId)
  entries.set(record.taskId, entry)
  if (expired) {
    entry.snapshot = expiredSnapshot(entry)
    sendLaunchEvent(entry, undefined, 'journal_expired')
    return entry
  }
  sendLaunchEvent(entry, undefined, 'journal_accepted')
  void start(entry)
  return entry
}

function enrichDirectTurnTelemetryContext(
  result: Exclude<ReturnType<typeof inspectEnvironmentTaskLaunch>, {status: 'missing'}>,
  task: EnvironmentTaskLaunchTask | undefined,
): Exclude<ReturnType<typeof inspectEnvironmentTaskLaunch>, {status: 'missing'}> | undefined {
  const {record} = result
  const seed = record.directTurnTelemetrySeed
  const context = seed ? resolveEnvironmentTaskTelemetryContext(seed, record.identity, task) : undefined
  if (!seed || seed.context || !context) return result

  const replaceFailure = replaceEnvironmentTaskLaunch(
    record.taskId,
    launch => ({
      ...launch,
      directTurnTelemetrySeed: {
        ...seed,
        context,
      },
    }),
    record,
  )
  if (replaceFailure && replaceFailure !== 'superseded') {
    // Optional telemetry enrichment must not block startup, but failed persistence remains diagnosable.
    // eslint-disable-next-line no-console
    console.error('Failed to persist environment task launch telemetry context', replaceFailure)
    return result
  }

  const latest = inspectEnvironmentTaskLaunch(record.taskId)
  return latest.status !== 'missing' && latest.record.launchId === record.launchId ? latest : undefined
}

function acquire(
  taskId: string,
  listener: () => void,
  task: EnvironmentTaskLaunchTask | undefined,
  awaitingAuthoritativeTask: boolean,
): LaunchEntry | undefined {
  let entry = entries.get(taskId)
  if (!entry) {
    const result = inspectEnvironmentTaskLaunch(taskId)
    if (result.status === 'missing') {
      checkedTaskIds.add(taskId)
      listener()
      return undefined
    }
    if (result.status === 'expired') {
      entry = createEntry(result.record, true, awaitingAuthoritativeTask, task)
    } else {
      const launch = enrichDirectTurnTelemetryContext(result, task)
      if (!launch) {
        checkedTaskIds.add(taskId)
        listener()
        return undefined
      }
      entry = createEntry(launch.record, launch.status === 'expired', awaitingAuthoritativeTask, task)
    }
  } else if (task) {
    entry.authoritativeTask = task
  }
  if (entry.releaseTimer) {
    clearTimeout(entry.releaseTimer)
    entry.releaseTimer = undefined
  }
  clearOwnerBoundary(entry)
  entry.consumers += 1
  entry.listeners.add(listener)
  return entry
}

function release(entry: LaunchEntry, listener: () => void): void {
  entry.listeners.delete(listener)
  entry.consumers = Math.max(0, entry.consumers - 1)
  if (entry.consumers > 0) return
  if (entry.snapshot.status === 'accepted') {
    observeUnclaimedFirstTurn(entry)
    return
  }
  scheduleRelease(entry)
}

const uncheckedState: EnvironmentTaskLaunchState = {status: 'unchecked'}
const unownedState: EnvironmentTaskLaunchState = {status: 'unowned'}

export function useEnvironmentTaskLaunch(
  taskId: string | undefined,
  task?: EnvironmentTaskLaunchTask,
  options?: {awaitAuthoritativeTask?: boolean; deferDestinationStartup?: boolean},
): EnvironmentTaskLaunchState {
  const destinationStartupEnabled =
    !!taskId &&
    !options?.deferDestinationStartup &&
    isFeatureEnabled('copilot_mission_control_environment_task_destination_startup')
  const inlineOwnerExpected = !!taskId && pendingInlineOwners.has(taskId)
  const launchOwned = !!taskId && entries.has(taskId)
  const launchEnabled = destinationStartupEnabled || inlineOwnerExpected || launchOwned
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!taskId || !launchEnabled) return () => {}
      if (!entries.has(taskId) && pendingInlineOwners.has(taskId)) {
        const listeners = waitingListeners.get(taskId) ?? new Set()
        listeners.add(listener)
        waitingListeners.set(taskId, listeners)
        return () => {
          const entry = entries.get(taskId)
          if (entry?.listeners.has(listener)) {
            release(entry, listener)
            return
          }
          listeners.delete(listener)
          if (listeners.size === 0) waitingListeners.delete(taskId)
        }
      }
      const entry = acquire(taskId, listener, task, options?.awaitAuthoritativeTask === true)
      return entry ? () => release(entry, listener) : () => {}
    },
    [launchEnabled, options?.awaitAuthoritativeTask, task, taskId],
  )
  const getSnapshot = useCallback(() => {
    if (!taskId || !launchEnabled) return unownedState
    const pending = pendingInlineOwners.get(taskId)
    if (pending) return pending.snapshot
    return entries.get(taskId)?.snapshot ?? (checkedTaskIds.has(taskId) ? unownedState : uncheckedState)
  }, [launchEnabled, taskId])
  const getServerSnapshot = useCallback(() => (launchEnabled ? uncheckedState : unownedState), [launchEnabled])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

export type EnvironmentTaskLaunchTaskResolver = (task: EnvironmentTaskLaunchTask | null) => void

export function captureEnvironmentTaskLaunchTaskResolver(
  taskId: string,
): EnvironmentTaskLaunchTaskResolver | undefined {
  const entry = entries.get(taskId)
  if (!entry || entry.disposed || !entry.awaitingAuthoritativeTask) return undefined

  const seed = entry.record.directTurnTelemetrySeed
  if (!seed || seed.context) {
    entry.awaitingAuthoritativeTask = false
    return undefined
  }

  const token = {
    taskId,
    launchId: entry.record.launchId,
    requestId: entry.record.requestId,
    telemetryRequestId: seed.requestId,
    attempt: entry.directTurnTelemetryAttempt,
  }

  return task => {
    if (
      entries.get(token.taskId) !== entry ||
      entry.disposed ||
      !entry.awaitingAuthoritativeTask ||
      entry.record.launchId !== token.launchId ||
      entry.record.requestId !== token.requestId ||
      entry.record.directTurnTelemetrySeed?.requestId !== token.telemetryRequestId ||
      entry.directTurnTelemetryAttempt !== token.attempt
    ) {
      return
    }

    const persisted = inspectEnvironmentTaskLaunch(token.taskId)
    if (
      persisted.status !== 'missing' &&
      (persisted.record.launchId !== token.launchId ||
        persisted.record.requestId !== token.requestId ||
        persisted.record.revision !== entry.record.revision)
    ) {
      resolvePendingTelemetry(entry, null)
      entry.awaitingAuthoritativeTask = false
      return
    }

    if (persisted.status === 'expired') {
      entry.authoritativeTask = task ?? undefined
      resolvePendingTelemetry(entry, null)
      entry.awaitingAuthoritativeTask = false
      return
    }

    const context = task ? resolveEnvironmentTaskTelemetryContext(seed, entry.record.identity, task) : undefined
    if (context && persisted.status !== 'missing') {
      const enriched = enrichDirectTurnTelemetryContext(persisted, task ?? undefined)
      if (!enriched) {
        resolvePendingTelemetry(entry, context)
        entry.awaitingAuthoritativeTask = false
        return
      }
      entry.record = enriched.record
    } else if (context) {
      entry.record = {
        ...entry.record,
        directTurnTelemetrySeed: {
          ...seed,
          context,
        },
      }
    }

    resolvePendingTelemetry(entry, context ?? null)
    entry.awaitingAuthoritativeTask = false
  }
}

export function __resetEnvironmentTaskLaunchManager(): void {
  for (const entry of entries.values()) {
    disposeEntry(entry)
  }
  entries.clear()
  checkedTaskIds.clear()
  pendingInlineOwners.clear()
  waitingListeners.clear()
}
